import { Base64 } from "js-base64"

// ----------------------------------------------------------------------------

const DEBUG_API =
	typeof process !== "undefined" && process.env.NODE_ENV !== "production"

const ACCESS_TOKEN = process.env.BUN_PUBLIC_MASTER_KEY || "change-me-master-key"

export const BASE_URL = "https://webapicogesergo.cogeserenergia.it"

// ----------------------------------------------------------------------------

export class APINotOkError extends Error {
	statusCode: number
	serverMessage: string | undefined
	errorKey: string | undefined

	constructor(statusCode: number, serverResponse?: Record<string, unknown>) {
		const message =
			(serverResponse?.message as string) || `HTTP status code: ${statusCode}`
		super(message)
		this.name = "APINotOkError"
		this.statusCode = statusCode
		this.serverMessage = serverResponse?.state as string | undefined
		this.errorKey = serverResponse?.key as string | undefined
	}
}

// ----------------------------------------------------------------------------
// Auth
// ----------------------------------------------------------------------------

export function signIn(email: string, password: string) {
	const encoded = Base64.encode(`${email}:${password}`)
	const headers = {
		Authorization: `Basic ${encoded}`,
	}
	const body = { access_token: ACCESS_TOKEN }

	return executePOSTRequest(`${BASE_URL}/auth`, null, body, headers)
}

// ----------------------------------------------------------------------------
// Users
// ----------------------------------------------------------------------------

export function getMe(token: string) {
	return executeGETRequest(`${BASE_URL}/users/me`, token)
}

export function getUsers(params: Record<string, unknown>, token: string) {
	const url = urlWithQueryParams(`${BASE_URL}/users`, params)
	return executeGETRequest(url, token)
}

export function getUser(id: string, token: string) {
	return executeGETRequest(`${BASE_URL}/users/${id}`, token)
}

export function createUser(body: Record<string, unknown>, token: string) {
	return executePOSTRequest(`${BASE_URL}/users`, token, body)
}

export function updateUser(
	id: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/users/${id}`, token, body)
}

export function deleteUser(id: string, token: string) {
	return executeDELETERequest(`${BASE_URL}/users/${id}`, token)
}

// ----------------------------------------------------------------------------
// Customers
// ----------------------------------------------------------------------------

export function getCustomers(params: Record<string, unknown>, token: string) {
	const url = urlWithQueryParams(`${BASE_URL}/customers`, params)
	return executeGETRequest(url, token)
}

export function getCustomer(id: string, token: string) {
	return executeGETRequest(`${BASE_URL}/customers/${id}`, token)
}

export function updateCustomer(
	id: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/customers/${id}`, token, body)
}

// ----------------------------------------------------------------------------
// Loyalty
// ----------------------------------------------------------------------------

export function getLoyaltyCards(
	params: Record<string, unknown>,
	token: string,
) {
	const url = urlWithQueryParams(`${BASE_URL}/loyalty/cards`, params)
	return executeGETRequest(url, token)
}

export function getLoyaltyCard(cardId: string, token: string) {
	return executeGETRequest(`${BASE_URL}/loyalty/cards/${cardId}`, token)
}

export function getCardTransactions(
	cardId: string,
	params: Record<string, unknown>,
	token: string,
) {
	const url = urlWithQueryParams(
		`${BASE_URL}/loyalty/cards/${cardId}/transactions`,
		params,
	)
	return executeGETRequest(url, token)
}

export function adjustPoints(
	cardId: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePOSTRequest(
		`${BASE_URL}/loyalty/cards/${cardId}/adjust`,
		token,
		body,
	)
}

export function recalculateBalance(cardId: string, token: string) {
	return executePOSTRequest(
		`${BASE_URL}/loyalty/cards/${cardId}/recalculate`,
		token,
		{},
	)
}

// ----------------------------------------------------------------------------
// Contracts
// ----------------------------------------------------------------------------

export function getContracts(params: Record<string, unknown>, token: string) {
	const url = urlWithQueryParams(`${BASE_URL}/contracts`, params)
	return executeGETRequest(url, token)
}

export function getContract(id: string, token: string) {
	return executeGETRequest(`${BASE_URL}/contracts/${id}`, token)
}

export function createContract(body: Record<string, unknown>, token: string) {
	return executePOSTRequest(`${BASE_URL}/contracts`, token, body)
}

export function updateContract(
	id: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/contracts/${id}`, token, body)
}

export function deleteContract(id: string, token: string) {
	return executeDELETERequest(`${BASE_URL}/contracts/${id}`, token)
}

// ----------------------------------------------------------------------------
// App Settings
// ----------------------------------------------------------------------------

export function getMobileSettings(token: string) {
	return executeGETRequest(`${BASE_URL}/app-settings/mobile`, token)
}

export function updateMobileSettings(
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/app-settings/mobile`, token, body)
}

// ----------------------------------------------------------------------------
// Reports
// ----------------------------------------------------------------------------

export function getReportNonConformingData(token: string) {
	return executeGETRequest(`${BASE_URL}/reports/non-conforming-data`, token)
}

export function getReportReferrals(
	params: Record<string, unknown>,
	token: string,
) {
	const url = urlWithQueryParams(`${BASE_URL}/reports/referrals`, params)
	return executeGETRequest(url, token)
}

export function getReportLoyaltyTransactions(
	params: Record<string, unknown>,
	token: string,
) {
	const url = urlWithQueryParams(
		`${BASE_URL}/reports/loyalty-transactions`,
		params,
	)
	return executeGETRequest(url, token)
}

// ----------------------------------------------------------------------------
// Rewards
// ----------------------------------------------------------------------------

export function getRewards(params: Record<string, unknown>, token: string) {
	const url = urlWithQueryParams(`${BASE_URL}/loyalty/rewards`, params)
	return executeGETRequest(url, token)
}

export function getReward(id: string, token: string) {
	return executeGETRequest(`${BASE_URL}/loyalty/rewards/${id}`, token)
}

export function createReward(body: Record<string, unknown>, token: string) {
	return executePOSTRequest(`${BASE_URL}/loyalty/rewards`, token, body)
}

export function updateReward(
	id: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/loyalty/rewards/${id}`, token, body)
}

export function deleteReward(id: string, token: string) {
	return executeDELETERequest(`${BASE_URL}/loyalty/rewards/${id}`, token)
}

export function adminRedeemReward(
	rewardId: string,
	customerId: string,
	token: string,
) {
	return executePOSTRequest(
		`${BASE_URL}/loyalty/rewards/${rewardId}/redeem-for`,
		token,
		{ customerId },
	)
}

export function confirmRedemption(redemptionId: string, token: string) {
	return executePOSTRequest(
		`${BASE_URL}/loyalty/redemptions/${redemptionId}/confirm`,
		token,
		{},
	)
}

export function cancelRedemption(redemptionId: string, token: string) {
	return executePOSTRequest(
		`${BASE_URL}/loyalty/redemptions/${redemptionId}/cancel`,
		token,
		{},
	)
}

// ----------------------------------------------------------------------------
// Loyalty Rules
// ----------------------------------------------------------------------------

export function getLoyaltyRules(token: string) {
	return executeGETRequest(`${BASE_URL}/loyalty/rules`, token)
}

export function createLoyaltyRule(
	body: Record<string, unknown>,
	token: string,
) {
	return executePOSTRequest(`${BASE_URL}/loyalty/rules`, token, body)
}

export function updateLoyaltyRule(
	code: string,
	body: Record<string, unknown>,
	token: string,
) {
	return executePUTRequest(`${BASE_URL}/loyalty/rules/${code}`, token, body)
}

export function deleteLoyaltyRule(code: string, token: string) {
	return executeDELETERequest(`${BASE_URL}/loyalty/rules/${code}`, token)
}

// ----------------------------------------------------------------------------
// Password Reset
// ----------------------------------------------------------------------------

export function requestPasswordReset(email: string) {
	return executePOSTRequest(`${BASE_URL}/password-resets`, null, { email })
}

export function verifyResetCode(email: string, code: string) {
	return executePOSTRequest(`${BASE_URL}/password-resets/verify`, null, {
		email,
		code,
	})
}

export function resetPassword(
	email: string,
	code: string,
	newPassword: string,
) {
	return executePUTRequest(`${BASE_URL}/password-resets`, null, {
		email,
		code,
		newPassword,
	})
}

// ----------------------------------------------------------------------------
// Private helpers
// ----------------------------------------------------------------------------

function executeGETRequest(
	url: string,
	token: string | null,
	extraHeaders?: Record<string, string>,
) {
	const headers: Record<string, string> = {
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		...extraHeaders,
	}
	return executeRequest(url, "get", null, headers)
}

function executePOSTRequest(
	url: string,
	token: string | null,
	body?: Record<string, unknown>,
	headers?: Record<string, string>,
) {
	const newHeaders: Record<string, string> = { ...headers }
	if (token) {
		newHeaders["Authorization"] = `Bearer ${token}`
	}
	return executeRequest(url, "post", body, newHeaders)
}

function executePUTRequest(
	url: string,
	token: string | null,
	body?: Record<string, unknown>,
	headers?: Record<string, string>,
) {
	const newHeaders: Record<string, string> = { ...headers }
	if (token) {
		newHeaders["Authorization"] = `Bearer ${token}`
	}
	return executeRequest(url, "put", body, newHeaders)
}

function executeDELETERequest(
	url: string,
	token: string | null,
	headers?: Record<string, string>,
) {
	const defaultHeaders: Record<string, string> = token
		? { Authorization: `Bearer ${token}` }
		: {}
	return executeRequest(url, "delete", null, headers || defaultHeaders)
}

// ----------------------------------------------------------------------------

function executeRequest(
	givenURL: string,
	method: string,
	givenBody?: Record<string, unknown> | null,
	givenHeaders?: Record<string, string>,
) {
	const headers: Record<string, string> = {
		Accept: "application/json",
		"Content-Type": "application/json",
		...givenHeaders,
	}

	let body: string | undefined
	if (givenBody) {
		body = JSON.stringify(givenBody)
		if (DEBUG_API) {
			console.debug("[API/REQUEST/BODY]", givenURL, givenBody)
		}
	}

	const requestData: RequestInit = { method, headers, body }

	if (DEBUG_API) {
		console.debug("[API/REQUEST]", givenURL, requestData)
	}

	return fetch(givenURL, requestData).then((response) => {
		if (DEBUG_API) {
			console.debug("[API/RESPONSE]", givenURL, response)
		}

		return response.text().then((text) => {
			let responseBody: unknown
			try {
				responseBody = JSON.parse(text)
			} catch {
				responseBody = text
			}

			if (DEBUG_API) {
				console.debug("[API/RESPONSE/BODY]", responseBody)
			}

			if (response.ok) {
				return responseBody
			} else {
				throw new APINotOkError(
					response.status,
					responseBody as Record<string, unknown>,
				)
			}
		})
	})
}

// ----------------------------------------------------------------------------

function urlWithQueryParams(
	url: string,
	params: Record<string, unknown>,
): string {
	const urlParams = Object.entries(params)
		.map(([k, v]) => {
			if (v === undefined || v === null) return null
			return `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
		})
		.filter(Boolean)
		.join("&")
	return urlParams ? `${url}?${urlParams}` : url
}
